/**
 * Whether a newer admin has been deployed than the one this tab is running
 * (the owner, 2026-10-11: reload by itself after an update, rather than
 * somebody testing yesterday's screens until they think to press Ctrl+Shift+R).
 *
 * A build names its entry script by a hash of its contents, so the page the
 * server hands out now names a different script once a deploy has landed. That
 * name is the whole version: nothing has to be written down at build time, and
 * a deploy that changed nothing in the admin is, correctly, no new version.
 */

import { useEffect, useState } from "react";

const ENTRY = /\/assets\/index-[\w-]+\.js/;

/** The entry script this page loaded, or null where it was not a built page
 * (the dev server's), which then never asks. */
export function runningEntry(doc: Document = document): string | null {
  for (const script of Array.from(doc.querySelectorAll<HTMLScriptElement>("script[src]"))) {
    const found = script.getAttribute("src")?.match(ENTRY);
    if (found) return found[0];
  }
  return null;
}

/** The entry script the server is handing out now. */
export async function servedEntry(): Promise<string | null> {
  const answer = await fetch("/", { cache: "no-store", credentials: "same-origin" });
  if (!answer.ok) return null;
  return (await answer.text()).match(ENTRY)?.[0] ?? null;
}

/** True once the server hands out a different admin from this one: asked every
 * few minutes, and whenever the tab is looked at again. */
export function useNewVersion(everyMs = 5 * 60_000): boolean {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    const running = runningEntry();
    if (!running) return;
    let live = true;
    const check = () => {
      servedEntry()
        .then((served) => {
          if (live && served && served !== running) setNewer(true);
        })
        // Offline or mid-deploy: the next look will tell.
        .catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const timer = window.setInterval(check, everyMs);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [everyMs]);
  return newer;
}

/** A part of the old build that the deploy removed cannot be loaded: reload
 * into the new one, at most once a minute so a real outage cannot loop. */
export function reloadOnStaleChunks(storage: Storage = sessionStorage): void {
  window.addEventListener("vite:preloadError", (event) => {
    const last = Number(storage.getItem("fm-stale-reload") ?? 0);
    if (Date.now() - last < 60_000) return;
    event.preventDefault();
    storage.setItem("fm-stale-reload", String(Date.now()));
    window.location.reload();
  });
}
